import React from 'react';
import { AirportDetails, AppState } from 'acscp/store';
import { Leg } from 'acscp/api/models/leg';
import { DurationStyle } from 'acscp/components/common/helpers/formatMoments';
import { Duration } from 'acscp/components/common/duration';
import { FormattedMessage } from 'react-intl';
import style from './schedule.module.scss';
import labeledItemStyle from 'acscp/components/common/labeledItem.module.scss';
import { getDepartureAirportFromLeg } from 'acscp/components/common/helpers/airportHelpers';
import { LabeledItem } from 'acscp/components/common/labeledItem';
import { Title } from 'acscp/components/itinerary/collapsableSections';
import { withCollapsable } from 'acscp/components/common/collapsable';
import { addZeroWidthSpacesAroundSeparatorCharacters } from 'acscp/components/common/helpers/stringHelpers';
import { VisaMessage } from './visaMessage';
import { DateStyle, FormattedDate } from 'acscp/components/common/formattedDate';
import classNames from 'classnames';
import { LocaleCode } from 'acscp/translations';
import { connect } from 'react-redux';
import { TimeType } from 'acscp/api/models/customTypes/timeType';

interface ScheduleProps {
    legs: Leg[];
    airports: AirportDetails[];
}

interface InjectedStateProps {
    locale: LocaleCode;
}

const MobileSchedule: React.SFC<ScheduleProps & InjectedStateProps> = ({ legs, airports, locale }) => {
    return (
        <div className={style.MobileSchedule}>
            {legs.map((leg, index) => {
                const departureAirport = getDepartureAirportFromLeg(airports, leg);
                return (
                    <div key={leg.id} className={style.Leg}>
                        <div className={labeledItemStyle.Label}>
                            <FormattedDate date={leg.departureDate} style={DateStyle.Long} />
                        </div>
                        <LabeledItem
                            label={
                                <FormattedMessage
                                    id={'components.itinerary.schedule.mobileDepart'}
                                    defaultMessage={'Depart'}
                                />}
                        >
                            {addZeroWidthSpacesAroundSeparatorCharacters(leg.departureLocation)}
                        </LabeledItem>
                        <LabeledItem
                            label={leg.departureTimeType === TimeType.UTC
                                ? <FormattedMessage
                                    id={'components.itinerary.schedule.mobileDepartureUTC'}
                                    defaultMessage={'Departure (UTC)'}
                                />
                                : <FormattedMessage
                                    id={'components.itinerary.schedule.mobileDepartureLocal'}
                                    defaultMessage={'Departure (Local)'}
                                />
                            }
                        >
                            {(leg.departureDate.getLocallyFormattedTime(locale))}
                        </LabeledItem>
                        <LabeledItem
                            label={
                                <FormattedMessage
                                    id={'components.itinerary.schedule.mobileReportTime'}
                                    defaultMessage={'Report Time'}
                                />}
                        >
                            {departureAirport && departureAirport.reportTime}
                        </LabeledItem>
                        <LabeledItem
                            label={
                                <FormattedMessage
                                    id={'components.itinerary.schedule.mobileArrive'}
                                    defaultMessage={'Arrive'}
                                />}
                        >
                            {addZeroWidthSpacesAroundSeparatorCharacters(leg.arrivalLocation)}
                        </LabeledItem>
                        <LabeledItem
                            label={leg.arrivalTimeType === TimeType.UTC
                                ? <FormattedMessage
                                    id={'components.itinerary.schedule.mobileArrivalUTC'}
                                    defaultMessage={'Arrival (UTC)'}
                                />
                                : <FormattedMessage
                                    id={'components.itinerary.schedule.mobileArrivalLocal'}
                                    defaultMessage={'Arrival (Local)'}
                                />
                            }
                        >
                            {leg.arrivalDate.getLocallyFormattedTime(locale)}
                        </LabeledItem>
                        <LabeledItem
                            label={
                                <FormattedMessage
                                    id={'components.itinerary.schedule.mobileFlightTime'}
                                    defaultMessage={'Flight Time'}
                                />}
                        >
                            <Duration duration={leg.flightTimeDuration} style={DurationStyle.Long} />
                        </LabeledItem>
                    </div>
                );
            }
            )}
        </div>
    );
};

const TabletUpSchedule: React.SFC<ScheduleProps & InjectedStateProps> = ({ legs, airports, locale }) => {
    const numberOfPassengers = legs[0].numberOfPassengers;
    const firstAirport = getDepartureAirportFromLeg(airports, legs[0]);
    return (
        <div className={style.TabletSchedule}>
            <div className={style.NumberOfPassengers}>
                <FormattedMessage
                    id={'components.itinerary.schedule.passengers'}
                    defaultMessage={'Passengers'}
                />: {numberOfPassengers}
            </div>
            {legs.map((leg, index) => {
                const departureAirport = getDepartureAirportFromLeg(airports, leg);
                return (
                    <div key={leg.id} className={style.LegTable}>
                        <div className={style.LegRow}>
                            <div className={style.RowLabel}>
                                <FormattedDate date={leg.departureDate} style={DateStyle.ExtraLong} />
                            </div>
                            <div className={style.RowLabel}>
                                {
                                    leg.departureTimeType === TimeType.UTC
                                        ? <FormattedMessage
                                            id={'components.itinerary.schedule.UTCTime'}
                                            defaultMessage={'UTC TIME'}
                                        />
                                        : <FormattedMessage
                                            id={'components.itinerary.schedule.localTime'}
                                            defaultMessage={'LOCAL TIME'}
                                        />
                                }
                            </div>
                            <div className={style.RowLabel}>
                                <FormattedMessage
                                    id={'components.itinerary.schedule.flightTime'}
                                    defaultMessage={'FLIGHT TIME'}
                                />
                            </div>
                        </div>
                        <div className={style.LegRow}>
                            <LabeledItem
                                label={
                                    <FormattedMessage
                                        id={'components.itinerary.schedule.depart'}
                                        defaultMessage={'Depart'}
                                    />}
                            >
                                {addZeroWidthSpacesAroundSeparatorCharacters(leg.departureLocation)}
                            </LabeledItem>
                            <div>
                                {leg.departureDate.getLocallyFormattedTime(locale)}
                            </div>
                            <div>
                                <Duration duration={leg.flightTimeDuration} style={DurationStyle.Long} />
                            </div>
                        </div>
                        <div className={style.LegRow}>
                            <LabeledItem
                                label={
                                    <FormattedMessage
                                        id={'components.itinerary.schedule.arrive'}
                                        defaultMessage={'Arrive'}
                                    />}
                            >
                                {addZeroWidthSpacesAroundSeparatorCharacters(leg.arrivalLocation)}
                            </LabeledItem>
                            <div>
                                {leg.arrivalDate.getLocallyFormattedTime(locale)}
                            </div>
                        </div>
                    </div>);
            }
            )}
        </div>
    );
};

const ScheduleContent: React.SFC<ScheduleProps & InjectedStateProps & {displayVisaMessage: boolean, className?: string }> =
    ({legs, airports, locale, displayVisaMessage, className}) => (
    <div className={classNames(style.Container, className)}>
        <MobileSchedule legs={legs} airports={airports} locale={locale} />
        <TabletUpSchedule legs={legs} airports={airports} locale={locale} />
        {displayVisaMessage && <VisaMessage />}
    </div>
);

function mapStateToProps(state: AppState): InjectedStateProps {
    return {
        locale: state.intl.locale
    };
}

const Schedule = connect(mapStateToProps)(withCollapsable(
    (
        <Title className={style.MobileOnly}>
            <FormattedMessage
                id={'components.itinerary.scheduleTitle'}
                defaultMessage={'SCHEDULE'}
            />
        </Title>
    ),
    ScheduleContent));

export { Schedule };
